import { useMemo } from "react";
import { useAuth } from "./useAuth";

/**
 * Returns the currently authenticated user in the shape existing UI components expect:
 *   { firstName, lastName, email, user_id, has_resources_access, subscription_status, ... }
 *
 * If nobody is signed in, returns a safe placeholder so components can still render
 * (they should be behind <ProtectedRoute /> if they need the real user).
 *
 * The returned object is memoised against the underlying `user` reference so that
 * consumers using it as a `useEffect` dependency don't fall into a render loop.
 */
const splitName = (full = "") => {
  const parts = full.trim().split(/\s+/);
  if (!parts[0]) return { firstName: "", lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
};

const PLACEHOLDER = { firstName: "", lastName: "", email: "" };

/**
 * Compatibility shim for legacy callers that used to update a mock user in
 * localStorage. In Stage 1 the profile is read-only against the real signed-in
 * user, so this is a no-op. Wire real "edit profile" endpoints in a later stage.
 */
export const setUser = () => {};

export default function useUser() {
  const { user } = useAuth();
  return useMemo(() => {
    if (!user || typeof user !== "object") return PLACEHOLDER;
    const { firstName, lastName } = splitName(user.name || "");
    return { ...user, firstName, lastName };
  }, [user]);
}
